import Vue from 'vue'
import Vuex from 'vuex'
import { ModuleData } from '@/models/ModuleData'
import { Module } from '@/models/Module'
import { ModuleDataItem } from '@/models/ModuleDataItem'
import { Modules } from '@/models/Modules'
import { Pages } from '@/models/Pages'
import { Page } from '@/models/Page'
import { Category } from '@/models/Category'
import { Categories } from '@/models/Categories'
// import { ModuleData, Module, ModuleDataItem, Modules } from '@/models/Module.js'
import axios from 'axios'

Vue.use(Vuex)

interface TableRow {
	[key: string]: any
}

export default new Vuex.Store({
	state: {
		windowWidth: 0,
		loading: true,
		modules: new Modules(),
		currentModule: new Module(),
		moduleData: new ModuleData(),
		tableView: true,
		pages: new Pages(),
		categories: new Categories(),
	},
	getters: {
		isMobile: (state) => state.windowWidth <= 992,
		modules: (state) => state.modules,
		getModule: (state) => (name: string) => {
			if (state.modules.models.length > 0) {
				const foundModule = state.modules.models.find((item: Module) => item.name === name)
				if (foundModule) {
					return foundModule
				}
			}
			return new Module()
		},
		moduleId: (state) => (name: string) => {
			const found = state.modules.models.find((item: Module) => item.name === name)
			if (found) {
				return found.id
			}
			return null
		},
		moduleItem: (state) => (id: string) => {
			const found = state.moduleData.models.find((item: ModuleDataItem) => item.id === parseInt(id, 10))
			if (found) {
				return found
			}
			return null
		},
		moduleHeaders: (state) => {
			return state.currentModule.fields
		},
		currentModule: (state) => state.currentModule,
		moduleData: (state) => state.moduleData,
		loading: (state) => state.loading,
		tableView: (state) => state.tableView,
		pages: (state) => state.pages,
		currentPage: (state) => (id: number) => {
			const foundPage = state.pages.models.find((page: any) => page.id === id)
			if (foundPage) {
				return foundPage
			}
			return new Page()
		},
		categories: (state) => state.categories,
		currentCategory: (state) => (id: number) => {
			const foundCategory = state.categories.models.find((category: any) => category.id === id)
			if (foundCategory) {
				return foundCategory
			}
			return new Category()
		},
	},
	mutations: {
		windowWidth(state, payload: number) {
			state.windowWidth = payload
		},
		setModules(state, payload: Modules) {
			state.modules = payload
		},
		currentModule(state, payload: Module) {
			state.currentModule = payload
		},
		moduleData(state, payload: ModuleData) {
			state.moduleData = payload
		},
		loading(state, payload: boolean) {
			state.loading = payload
		},
		tableView(state, payload: boolean) {
			state.tableView = payload
		},
		pages(state, payload: Pages) {
			state.pages = payload
		},
		addPage(state, payload: Page) {
			state.pages.models.push(payload)
		},
		deletePage(state) {
			const pages = state.pages.models
			pages.forEach((page: any) => {
				if (!page.id) {
					pages.splice(pages.indexOf(page), 1)
				}
			})
		},
		categories(state, payload: Categories) {
			state.categories = payload
		},
		addCategory(state, payload: Category) {
			state.categories.models.push(payload)
		},
		deleteCategory(state) {
			const categories = state.categories.models
			categories.forEach((category: any) => {
				if (!category.id) {
					categories.splice(categories.indexOf(category), 1)
				}
			})
		},
	},
	actions: {
		initialFetch({ dispatch, commit }) {
			return new Promise((resolve, reject) => {
				const promises = [dispatch('setModules'), dispatch('pages'), dispatch('categories')]
				commit('loading', true)
				Promise.all(promises).then(() => {
					commit('loading', false)
					resolve()
				}, (error: any) => reject(error))
			})
		},
		setModules({ commit }) {
			return new Promise((resolve, reject) => {
				const modules = new Modules()
				modules.fetch().then((response: any) => {
					commit('setModules', modules)
					resolve()
				}, (error: any) => {
					reject(error)
				})
			})
		},
		currentModule({ commit, state }, payload: number) {
			return new Promise((resolve, reject) => {
				const currentModule = state.currentModule
				currentModule.set('id', payload)
				currentModule.fetch().then(() => {
					commit('currentModule', currentModule)
					resolve()
				}, (error: any) => {
					reject(error)
				})
			})
		},
		moduleData({ commit, state }, payload: string) {
			return new Promise((resolve, reject) => {
				const moduleData = state.moduleData
				moduleData.set('name', payload)
				moduleData.fetch().then(() => {
					commit('moduleData', moduleData)
					resolve()
				}, (error: any) => {
					reject(error)
				})
			})
		},
		pages({ commit, state }) {
			return new Promise((resolve, reject) => {
				const pages = state.pages
				pages.fetch().then(() => {
					commit('pages', pages)
					resolve()
				},
				(error: any) => reject(error))
			})
		},
		savePage({ commit }, payload: Page) {
			return new Promise((resolve, reject) => {
				const newPage = payload.id ? false : true
				payload.save().then(() => {
					if (newPage) {
						commit('addPage', payload)
					}
					resolve()
				}, (error: any) => {
					reject(error)
				})
			})
		},
		deletePage({ commit }, payload: Page) {
			return new Promise((resolve, reject) => {
				payload.delete().then(() => {
					commit('deletePage')
					resolve()
				}, (error: any) => reject(error))
			})
		},
		categories({ commit, state }) {
			return new Promise((resolve, reject) => {
				const categories = state.categories
				categories.fetch().then(() => {
					commit('categories', categories)
					resolve()
				}, (error: any) => reject(error))
			})
		},
		saveCategory({ commit }, payload: Category) {
			return new Promise((resolve, reject) => {
				const newCategory = payload.id ? false : true
				payload.save().then(() => {
					if (newCategory) {
						commit('addCategory', payload)
					}
					resolve()
				}, (error: any) => reject(error))
			})
		},
		deleteCategory({ commit }, payload: Category) {
			return new Promise((resolve, reject) => {
				payload.delete().then(() => {
					commit('deleteCategory')
					resolve()
				}, (error: any) => reject(error))
			})
		},
		login({ commit }, payload: any) {
			return new Promise((resolve, reject) => {
				const ajax = axios.create({
					baseURL: process.env.VUE_APP_API_ROOT || 'https://api.mygosystems.com/v1/',
					headers: {
						'Content-Type': 'application/json',
						'Accept': 'application/json',
						'x-client': payload.client,
					},
					auth: payload.auth,
				})

				ajax.get('modules').then((response) => {
					localStorage.setItem('authKey', response.config.headers.Authorization)
					localStorage.setItem('client', response.config.headers['x-client'])

					const jsonClientList: any = localStorage.getItem('clientList')
					const clientList: object[] = JSON.parse(jsonClientList) || []
					let exists: boolean = false

					clientList.forEach((client: any) => {
						if (client.client === response.config.headers['x-client']) {
							exists = true
						}
					})
					if (!exists) {
						clientList.push({ client: response.config.headers['x-client'], authKey: response.config.headers.Authorization })
						localStorage.setItem('clientList', JSON.stringify(clientList))
					}

					resolve()
				}, (error: any) => {
					reject(error)
				})
			})
		},
	},
})
